import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Layout from "@/components/Layout";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { BookOpen, Lock, Sparkles, GraduationCap, FileCheck, ArrowRight, Building2 } from "lucide-react";

/**
 * Public course listing page for "Mastering Form 1120 for Preparers."
 * Chapters 1-3 and 3 practice returns are free preview (no login required).
 */
export default function Course1120List() {
  const { user } = useAuth();
  const [chapters, setChapters] = useState([]);
  const [practice, setPractice] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [ch, pr] = await Promise.all([
          api.get("/course1120"),
          api.get("/course1120/practice"),
        ]);
        setChapters(ch.data);
        setPractice(pr.data);
      } finally { setLoading(false); }
    })();
  }, []);

  const isPaid = user?.has_1120_access || user?.role === "admin";

  return (
    <Layout>
      <div className="max-w-6xl mx-auto px-4 md:px-6 py-10 md:py-14" data-testid="course-1120-list">
        <div className="mb-10">
          <div className="label-caps text-primary flex items-center gap-2">
            <Building2 size={14} /> Corporate Track
          </div>
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl mt-3 leading-none">
            Mastering Form 1120
            <span className="block text-primary italic">for Preparers.</span>
          </h1>
          <p className="text-muted-foreground mt-4 max-w-2xl">
            13 chapters. Every line of Form 1120 walked in plain English — plus Schedules L, M-1, M-2,
            §179 and bonus depreciation, DRD, PHC and Accumulated Earnings traps, and a realistic
            practice return per chapter. Ends with a 20-question exam and a printable certificate.
          </p>

          {!isPaid && (
            <div className="mt-6 rounded-lg border border-primary/30 bg-primary/5 p-5 flex flex-wrap items-center gap-4" data-testid="course-1120-preview-banner">
              <div className="flex-1 min-w-[220px]">
                <div className="label-caps text-primary flex items-center gap-2">
                  <Sparkles size={12} /> Free preview
                </div>
                <div className="text-sm mt-1">
                  Read <strong>Chapters 1–3</strong> and try <strong>3 real practice returns</strong> — no login, no card required.
                </div>
              </div>
              <Button asChild data-testid="course-1120-buy-btn" className="min-w-[200px]">
                <Link to="/pricing?plan=corporate">Enroll — $250 <ArrowRight size={16} className="ml-1" /></Link>
              </Button>
            </div>
          )}

          {isPaid && (
            <div className="mt-6 rounded-lg border border-primary/30 bg-primary/5 p-5 flex flex-wrap items-center gap-3" data-testid="course-1120-enrolled-banner">
              <GraduationCap size={20} className="text-primary" />
              <div className="flex-1">
                <div className="font-medium">You're enrolled in Mastering Form 1120.</div>
                <div className="text-sm text-muted-foreground">All 13 chapters, practice returns, and the final exam are unlocked.</div>
              </div>
              <Button asChild variant="outline" data-testid="course-1120-exam-link">
                <Link to="/course/1120/exam">Take the final exam →</Link>
              </Button>
            </div>
          )}
        </div>

        {loading ? (
          <div className="text-muted-foreground">Loading course…</div>
        ) : (
          <>
            <div className="label-caps mb-3 flex items-center gap-2 text-primary"><BookOpen size={14} /> Chapters</div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 mb-12">
              {chapters.map(c => (
                <ChapterCard key={c.id} chapter={c} />
              ))}
            </div>

            <div className="label-caps mb-3 flex items-center gap-2 text-primary"><FileCheck size={14} /> Practice Returns — On the Actual Form</div>
            <p className="text-sm text-muted-foreground mb-4 max-w-2xl">
              Every practice return uses real Form 1120 line numbers (Line 1c, Line 11, Line 29b, Schedule L Line 15/25, M-1 Line 10, M-2 Line 8…) so you learn exactly how to input numbers into tax software or on paper.
            </p>
            <div className="grid md:grid-cols-2 gap-4">
              {practice.map(p => (
                <PracticeCard key={p.id} case={p} />
              ))}
            </div>
          </>
        )}
      </div>
    </Layout>
  );
}

function ChapterCard({ chapter }) {
  const locked = chapter.locked;
  const Wrapper = locked ? "div" : Link;
  const props = locked ? {} : { to: `/course/1120/chapter/${chapter.id}` };
  return (
    <Wrapper
      {...props}
      className={`block ${locked ? "cursor-not-allowed" : ""}`}
      data-testid={`chapter-1120-card-${chapter.id}`}
    >
      <Card className={`p-5 h-full transition-colors ${locked ? "bg-secondary/30 opacity-70" : "hover:border-primary/40"}`}>
        <div className="flex items-start justify-between gap-2 mb-2">
          <div className="label-caps text-[10px] text-primary">Chapter {chapter.id}</div>
          {chapter.free_preview && !locked && <span className="text-[10px] font-semibold bg-primary/10 text-primary rounded px-2 py-0.5">FREE PREVIEW</span>}
          {locked && <Lock size={14} className="text-muted-foreground" />}
        </div>
        <div className="font-serif text-lg leading-tight mb-2">{chapter.title}</div>
        <div className="text-xs text-muted-foreground line-clamp-3">{chapter.summary}</div>
        <div className="text-[10px] text-muted-foreground mt-3 uppercase tracking-widest">
          {chapter.reading_time} min · {chapter.category}
        </div>
      </Card>
    </Wrapper>
  );
}

function PracticeCard({ case: p }) {
  const locked = p.locked;
  const Wrapper = locked ? "div" : Link;
  const props = locked ? {} : { to: `/course/1120/practice/${p.id}` };
  return (
    <Wrapper
      {...props}
      className={`block ${locked ? "cursor-not-allowed" : ""}`}
      data-testid={`practice-1120-card-${p.id}`}
    >
      <Card className={`p-5 h-full transition-colors ${locked ? "bg-secondary/30 opacity-70" : "hover:border-primary/40"}`}>
        <div className="flex items-start justify-between gap-2 mb-2">
          <div className="label-caps text-[10px] text-primary">Practice · Chapter {p.chapter_id}</div>
          {p.free_preview && !locked && <span className="text-[10px] font-semibold bg-primary/10 text-primary rounded px-2 py-0.5">FREE PREVIEW</span>}
          {locked && <Lock size={14} className="text-muted-foreground" />}
        </div>
        <div className="font-serif text-base mb-2 leading-tight">{p.title}</div>
        <div className="text-xs text-muted-foreground line-clamp-3">{p.scenario}</div>
      </Card>
    </Wrapper>
  );
}
